import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {IFiltersSliceState} from '../../filters/filtersSlice';
import {RootState} from '../../store';
import {getDistribution} from './distributionComponentAPI';

interface IDistributionComponentState {
  apiResponse: IDistributionComponentAPIResponse
  chartCategories: string[]
  chartDataPoints: any[]
  chartHTMLSubtitle: string
  columnChartDataPoints: any[]
  isLoading: boolean
  hasError: boolean
  changeSeriesButtonText: string
  firstLoadFulfilled: boolean
  statisticLabel: string
}

interface IData {
  y: number
  filter: string
  filterLabel: string
  question: string
}

interface IDataPoint {
  name: string
  data: IData[]
}

export interface IDistributionComponentAPIResponse {
  values: IValue[]
  stat: string
  visualization: string
  label: string
  small_text: string
  statistic: string
  segmentName: string
  segmentCategory: string
  surveyLabel: string
  periodLabel: string
  noDataMessage: string
  factor: number
  linkText: boolean
  displayLink: boolean
  statVal: number
  surveyCount: string
}

interface IValue {
  column: number
  label: string
}

const initialState: IDistributionComponentState = {
  apiResponse: {
    values: [],
    stat: '',
    visualization: '',
    label: '',
    small_text: '',
    statistic: '',
    segmentName: '',
    segmentCategory: '',
    surveyLabel: '',
    periodLabel: '',
    noDataMessage: 'No data for the selected criteria.',
    factor: 0,
    linkText: false,
    displayLink: false,
    statVal: 0,
    surveyCount: '',
  },
  chartCategories: [],
  chartDataPoints: [],
  columnChartDataPoints: [],
  chartHTMLSubtitle: '',
  changeSeriesButtonText: 'Full Detail',
  isLoading: false,
  hasError: false,
  firstLoadFulfilled: false,
  statisticLabel: '',
}

export const loadDistribution = createAsyncThunk(
  'distributionComponent/get',
  async (filters: IFiltersSliceState) => {
    const response = await getDistribution(filters);
    return response;
  }
)

export const distributionComponentSlice = createSlice({
  name: 'distributionComponent',
  initialState,
  reducers: {
    setHTMLSubtitle: (state: IDistributionComponentState) => {
      const apiResponse = state.apiResponse;
      if (apiResponse) {
        let legend = `<small class="center-align">${apiResponse.small_text} | ${apiResponse.surveyLabel} | ${apiResponse.surveyCount} responses | ${apiResponse.periodLabel}`;
        if (apiResponse.segmentName !== '') {
          legend += ` | ${apiResponse.segmentCategory}: ${apiResponse.segmentName}`;
        }
        legend += '</small>';
        state.chartHTMLSubtitle = legend;
      }
      return state;
    },
    setChartData: (state: IDistributionComponentState) => {
      state.chartCategories = [];
      const rawValues = state.apiResponse.values;
      const dataPoints: IDataPoint[] =  [];
      const columnDataPoints: number[] = [];
      rawValues.forEach((value, index) => {
        const label = value.label;

        if (label !== '(n)') {
          columnDataPoints.push(value.column);
          state.chartCategories.push(value.label);
          // set chart data based on Change Series selection
          if (state.changeSeriesButtonText.toLowerCase() === 'full detail') {
            if (((label.search('T') > -1) || (label.search('M') > -1) || (label.search('B') > -1))) {
              dataPoints.push({
                name: label,
                data: [{
                  y: value.column,
                  filter: index.toString(),
                  filterLabel: label,
                  question: value.column.toString()
                }]
              });
            }
          } else if (state.changeSeriesButtonText.toLowerCase() === 'top/middle/bottom') {
            if (!((label.search('T') > -1) || (label.search('M') > -1) || (label.search('B') > -1))) {
              dataPoints.push({
                name: label,
                data: [{
                  y: value.column,
                  filter: index.toString(),
                  filterLabel: label,
                  question: value.column.toString()
                }]
              });
            }
          }
        }
      });

      state.chartDataPoints = dataPoints;
      state.columnChartDataPoints = columnDataPoints;
    },
    toggleChangeSeriesButton: (state) => {
      if (state.changeSeriesButtonText.toLowerCase() === 'top/middle/bottom') {
        state.changeSeriesButtonText = 'Full Detail';
      } else if (state.changeSeriesButtonText.toLowerCase() === 'full detail') {
        state.changeSeriesButtonText = 'Top/Middle/Bottom';
      }
      return state;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(loadDistribution.pending, (state) => {
      state.isLoading = true;
      state.hasError = false;
    });
    builder.addCase(loadDistribution.fulfilled, (state, action) => {
      if (action.payload) {
        state.apiResponse = action.payload;
        state.isLoading = false;
        state.hasError = false;
        state.statisticLabel = state.apiResponse.statistic.length ? `${state.apiResponse.statistic} ` : '';
        if (!state.firstLoadFulfilled) {
          state.firstLoadFulfilled = true;
        }
      }
    });
    builder.addCase(loadDistribution.rejected, (state, action) => {
      state.isLoading = false;
      if (action.error?.name !== 'AbortError') {
        state.hasError = true;
      }
    });
  },
});

export const selectDistributionComponentState = (state: RootState) => state.distributionComponent;
export const selectDistributionAPIResponse = (state: RootState) => state.distributionComponent.apiResponse;
export const selectIsDistributionDataLoading = (state: RootState) => state.distributionComponent.isLoading;
export const selectDistributionHasError = (state: RootState) => state.distributionComponent.hasError;
export const selectDistributionFirstLoadFulfilled = (state: RootState) => state.distributionComponent.firstLoadFulfilled;
export const selectDistributionLabel = (state: RootState) => state.distributionComponent.apiResponse.label;
export const selectDistributionChartData = (state: RootState) => state.distributionComponent.chartDataPoints;
export const selectChangeSeriesButtonText = (state: RootState) => state.distributionComponent.changeSeriesButtonText;
export const selectChartCategories = (state: RootState) => state.distributionComponent.chartCategories;
export const {setHTMLSubtitle, setChartData, toggleChangeSeriesButton} = distributionComponentSlice.actions
export default distributionComponentSlice.reducer;
